import React, { useMemo } from 'react';
import { ThemeTokens } from '../../theme/tokens';
import { useThemeTokens } from '../../theme/ThemeModeProvider';

// ─── Metadata Shape ──────────────────────────────────────────────────────────

export interface CombinedMetadata {
  visualizationType?: string;
  visualizationtype?: string;
  title?: string;
  x_axis_label?: string;
  y_axis_label?: string;
  bubble_label?: string;
  bubble_size_param?: string;
  x_axis_param?: string;
  y_axis_param?: string;
  [key: string]: any;
}

// ─── Shared Props Contract ───────────────────────────────────────────────────

/**
 * All visualization components implement this interface.
 * VisualizationResolver passes these props directly to whichever
 * component is selected — enabling true plug-and-play extensibility.
 */
export interface MultiColumnVizProps {
  headers: string[];
  data: any[];
  metadata: CombinedMetadata;
  itemLabel?: string;
  safeValue: (val: any) => React.ReactNode;
}

// ─── Utility: Row Value Resolver ─────────────────────────────────────────────

/**
 * Resolves a value from a data row (array or object) using multiple
 * fallback strategies:
 *  1. Array index (if row is an array)
 *  2. Explicit param key
 *  3. Label key as-is
 *  4. Lowercase + underscore-normalised label key
 */
export const getRowValue = (
  row: any,
  key: string,
  param: string | undefined,
  index: number
): any => {
  if (Array.isArray(row)) return row[index];
  if (param && row[param] !== undefined) return row[param];
  if (row[key] !== undefined) return row[key];
  const lowerKey = key.toLowerCase().replace(/ /g, '_');
  if (row[lowerKey] !== undefined) return row[lowerKey];
  return null;
};

// ─── Shared Theme Tokens ─────────────────────────────────────────────────────

/**
 * The chart palette for a theme, as hex: recharts writes these into SVG
 * attributes, where CSS variables do not resolve.
 */
export const chartThemeFor = (t: ThemeTokens) => ({
  bg: {
    card: t.surface,
    header: t.raised,
    deep: t.page,
  },
  border: {
    default: t.line,
  },
  text: {
    accent: t['accent-fg'],
    body: t['fg-body'],
    muted: t['fg-2'],
  },
  sentiment: {
    positive: t.success,
    negative: t.danger,
  },
  /** Marks the current value against a projection. */
  baseline: t.info,
  bubble: {
    primary: t['accent-fg'],
    secondary: t.success,
  },
});

export type ChartTheme = ReturnType<typeof chartThemeFor>;

/** The chart palette for the theme currently painted. */
export const useChartTheme = (): ChartTheme => {
  const tokens = useThemeTokens();
  // Stable per theme, so effects that rebuild a chart can depend on it.
  return useMemo(() => chartThemeFor(tokens), [tokens]);
};
